import React, { useState, useMemo } from 'react';
import {
  collections as collectionsApi,
  donations as donationsApi,
  expenses as expensesApi
} from '../../api/endpoints';
import api, { getList } from '../../api/client';
import { useFetchList, useSearch, usePagination , useNotification } from '../../hooks';
import { liveByStatus, voidedByStatus } from '../../utils/records';
import { formatDate, formatCurrencyPrecise } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  buildLedger, buildAuditLog, periodRange, summarisePeriod,
  headWiseSummary, buildBook, buildTrialBalance, buildAuditAlerts,
  REPORT_PERIODS, HIGH_VALUE_THRESHOLD
} from './ledger';
import {
  PageHeader, SearchBar, DataTable, Pagination, Input, Select, Badge, Button, TablePanel, PrintButton, Notification
} from '../../components/ui';
import type {
  Collection, Donation, Expense, LedgerEntry, BookEntry, AuditEntry, HeadTotal
} from '../../types';
import type { Column } from '../../components/ui';

const REPORT_VIEWS = [
  { value: 'dashboard', label: 'Financial Summary Dashboard' },
  { value: 'cash_book', label: 'Official Cash Book' },
  { value: 'bank_book', label: 'Official Bank Book' },
  { value: 'trial_balance', label: 'Trial Balance' },
  { value: 'pnl', label: 'Income & Expenditure Statement' },
  { value: 'balance_sheet', label: 'Balance Sheet' },
  { value: 'audit', label: 'Internal Audit & Void Log' }
];

/** Accounts stores nothing itself — it folds three ledgers into one list. */
type SourceRow = (Collection | Donation | Expense) & {
  __src: 'collections' | 'donations' | 'expenses';
};

const Accounts = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeTab, setActiveTab] = useState('ledger');
  const [reportView, setReportView] = useState('dashboard');
  const [reportPeriod, setReportPeriod] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Read-only view over three other modules' records.
  //
  // A failed request used to become an empty array, so a dead expenses endpoint
  // rendered a P&L with full income and zero expenditure and nothing said so.
  // The names of the sources that failed are carried out with the data instead,
  // and the screen warns before showing a total it cannot stand behind.
  const [missing, setMissing] = useState<string[]>([]);

  const { data: sources, loading } = useFetchList<SourceRow>(async (p, o) => {
    const wanted = [
      { key: 'collections' as const, label: 'collections', url: collectionsApi.url() },
      { key: 'donations' as const, label: 'donations', url: donationsApi.url() },
      { key: 'expenses' as const, label: 'expenses', url: expensesApi.url() }
    ];
    const settled = await Promise.allSettled(wanted.map((w) => api.get(w.url, o)));

    // A superseded load aborts its requests (StrictMode's double effect in
    // development, and any refetch). Those are not failures — reporting them
    // put a red "figures are incomplete" banner over a perfectly good ledger.
    if (o?.signal?.aborted) return [];

    const isAbort = (r: PromiseSettledResult<unknown>) =>
      r.status === 'rejected' && (r.reason as Error | undefined)?.name === 'AbortError';

    setMissing(
      wanted.filter((_, i) => settled[i].status === 'rejected' && !isAbort(settled[i]))
        .map((w) => w.label)
    );

    return wanted.flatMap((w, i) => {
      const outcome = settled[i];
      if (outcome.status !== 'fulfilled') return [];
      return getList<Collection | Donation | Expense>(outcome.value)
        .map((x): SourceRow => ({ ...x, __src: w.key } as SourceRow));
    });
  });

  const { ledger, auditLog } = useMemo(() => {
    // Live is "not VOID", never "=== ACTIVE" — a row saved before the column
    // existed has no status and must still be counted. See utils/records.
    const ofSource = (src: SourceRow['__src']) => sources.filter((x) => x.__src === src);
    return {
      ledger: buildLedger({
        collections: liveByStatus(ofSource('collections')) as Collection[],
        donations: liveByStatus(ofSource('donations')) as Donation[],
        expenses: liveByStatus(ofSource('expenses')) as Expense[]
      }),
      auditLog: buildAuditLog({
        collections: voidedByStatus(ofSource('collections')) as Collection[],
        donations: voidedByStatus(ofSource('donations')) as Donation[],
        expenses: voidedByStatus(ofSource('expenses')) as Expense[]
      })
    };
  }, [sources]);

  const { start, end } = useMemo(
    () => periodRange(reportPeriod, customStartDate, customEndDate),
    [reportPeriod, customStartDate, customEndDate]
  );

  const { notification, notify, dismiss } = useNotification();

  const summary = useMemo(() => summarisePeriod(ledger, start, end), [ledger, start, end]);
  const heads = useMemo(() => headWiseSummary(summary.inPeriod), [summary.inPeriod]);
  const cashBook = useMemo(() => buildBook(summary.inPeriod, 'Cash', summary.openingCash), [summary]);
  const bankBook = useMemo(() => buildBook(summary.inPeriod, 'Bank', summary.openingBank), [summary]);
  const trialBalance = useMemo(() => buildTrialBalance(summary, heads), [summary, heads]);
  const auditAlerts = useMemo(
    () => buildAuditAlerts(summary.inPeriod, auditLog, start, end),
    [summary.inPeriod, auditLog, start, end]
  );

  // --- General ledger tab ---
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(ledger, ['id', 'head', 'description']);
  const ledgerPage = usePagination(filtered);

  // --- Report tab pagination (one shared pager across the report views) ---
  const reportRows = useMemo<Array<BookEntry | AuditEntry>>(() => {
    if (reportView === 'cash_book') return cashBook;
    if (reportView === 'bank_book') return bankBook;
    if (reportView === 'audit') return auditLog;
    return [];
  }, [reportView, cashBook, bankBook, auditLog]);
  const reportPage = usePagination<BookEntry | AuditEntry>(reportRows);

  const money = (v) => formatCurrencyPrecise(v, '₹0.00');
  /** Shared padding for the hand-built statement tables (trial balance, balance sheet). */
  const cellBase = { padding: '10px 15px', fontSize: t.fontSize.md };
  const amountCell = (r) => (
    <span style={{ fontWeight: 'bold', color: r.type === 'Income' ? t.income : t.expense }}>
      {money(r.amount)}
    </span>
  );

  const ledgerColumns: Array<Column<LedgerEntry>> = [
    { key: 'id', header: 'Voucher ID', nowrap: true, render: (r) => <strong>{r.id}</strong> },
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'head', header: 'Account Head', render: (r) => <Badge>{r.head}</Badge> },
    { key: 'description', header: 'Particulars' },
    { key: 'mode', header: 'Mode', render: (r) => <Badge tone="neutral">{r.mode}</Badge> },
    {
      key: 'type',
      header: 'Type',
      render: (r) => <Badge tone={r.type === 'Income' ? 'success' : 'error'}>{r.type}</Badge>
    },
    { key: 'amount', header: 'Amount', align: 'right', nowrap: true, render: amountCell }
  ];

  /** Which document each report prints as; each has its own columns and totals. */
  const REPORT_DOCUMENTS: Record<string, string> = {
    dashboard: 'FINANCIAL_SUMMARY',
    cash_book: 'CASH_BOOK',
    bank_book: 'BANK_BOOK',
    trial_balance: 'TRIAL_BALANCE',
    pnl: 'INCOME_EXPENDITURE',
    balance_sheet: 'BALANCE_SHEET',
    audit: 'ACCOUNTS_AUDIT_LOG'
  };

  /** The head-wise lines shared by the summary and the income & expenditure statement. */
  const headRows = () => [
    ...heads.income.map((h) => ({ section: 'Income', particulars: h.head, amount: money(h.amount) })),
    ...heads.expense.map((h) => ({ section: 'Expenditure', particulars: h.head, amount: money(h.amount) }))
  ];

  /**
   * The chosen statement as its document's values: title, period, every row
   * and the totals printed under it. Each branch reads the same computed
   * figures the screen is showing, so paper and screen cannot disagree, and
   * every row prints — not only the page on screen.
   */
  const reportPrintData = () => {
    const title = REPORT_VIEWS.find((v) => v.value === reportView)?.label || 'Financial Report';
    const common = { title, reference: periodLabel };

    if (reportView === 'cash_book' || reportView === 'bank_book') {
      const isCash = reportView === 'cash_book';
      const book = isCash ? cashBook : bankBook;
      const sum = (type: string) => book.filter((r) => r.type === type).reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
      return {
        ...common,
        rowCount: book.length,
        openingBalance: money(isCash ? summary.openingCash : summary.openingBank),
        totalReceipts: money(sum('Income')),
        totalPayments: money(sum('Expense')),
        closingBalance: money(isCash ? summary.closingCash : summary.closingBank),
        rows: book.map((r) => ({
          date: formatDate(r.date),
          voucherId: r.id,
          particulars: r.description || r.head || '',
          head: r.head,
          // A book shows money in and money out in separate columns; the row's
          // own type says which side it belongs on.
          receipt: r.type === 'Income' ? money(r.amount) : '',
          payment: r.type === 'Expense' ? money(r.amount) : '',
          amount: money(r.amount),
          mode: r.mode,
          balance: money(r.runningBalance)
        }))
      };
    }

    if (reportView === 'trial_balance') {
      return {
        ...common,
        rowCount: trialBalance.rows.filter((r) => !r.isSection).length,
        totalDebit: money(trialBalance.totalDebit),
        totalCredit: money(trialBalance.totalCredit),
        balanced: trialBalance.balanced ? 'Yes — debit and credit agree' : 'No — debit and credit do not agree',
        // Section headings print as their own line, as on screen.
        rows: trialBalance.rows.map((r) => ({
          particulars: r.isSection ? String(r.label).toUpperCase() : r.label,
          debit: r.debit === undefined ? '' : money(r.debit),
          credit: r.credit === undefined ? '' : money(r.credit),
          amount: r.isSection ? '' : money(r.debit ?? r.credit ?? 0)
        }))
      };
    }

    if (reportView === 'balance_sheet') {
      const rows = [
        { section: 'Liabilities & Funds', particulars: 'Opening Balance', amount: money(summary.openingFund) },
        {
          section: 'Liabilities & Funds',
          particulars: summary.surplusDeficit >= 0 ? 'Add: Surplus' : 'Less: Deficit',
          amount: money(summary.surplusDeficit)
        },
        { section: 'Liabilities & Funds', particulars: 'Closing General Fund', amount: money(summary.closingFund) },
        { section: 'Assets', particulars: 'Cash in Hand', amount: money(summary.closingCash) },
        { section: 'Assets', particulars: 'Cash at Bank', amount: money(summary.closingBank) }
      ];
      return {
        ...common,
        rowCount: rows.length,
        asOn: end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        totalLiabilities: money(summary.closingFund),
        totalAssets: money(summary.closingCash + summary.closingBank),
        rows
      };
    }

    if (reportView === 'audit') {
      return {
        ...common,
        rowCount: auditLog.length,
        highValueCount: `${auditAlerts.highValueCount} above ${money(HIGH_VALUE_THRESHOLD)}`,
        voidedCount: String(auditAlerts.voidedCount),
        rows: auditLog.map((r) => ({
          origId: r.origId,
          auditType: r.auditType,
          date: formatDate(r.date),
          amount: money(r.amount),
          deleteReason: r.deleteReason || '-',
          deleteRequestedBy: [r.deleteRequestedBy, r.deleteRequestedAt].filter(Boolean).join(' · ') || '-',
          deletedAt: r.deletedAt || '-',
          deleteApprovedBy: r.deleteApprovedBy || '-'
        }))
      };
    }

    // The summary dashboard and the income & expenditure statement: head-wise
    // lines and the period's figures.
    const rows = headRows();
    return {
      ...common,
      rowCount: rows.length,
      totalIncome: money(summary.totalIncome),
      totalExpense: money(summary.totalExpense),
      surplusDeficit: `${summary.surplusDeficit >= 0 ? 'Surplus' : 'Deficit'} ${money(summary.surplusDeficit)}`,
      openingCash: money(summary.openingCash),
      openingBank: money(summary.openingBank),
      closingCash: money(summary.closingCash),
      closingBank: money(summary.closingBank),
      closingFund: money(summary.closingFund),
      rows
    };
  };

  /** The General Ledger as filtered on screen, every entry, with its totals. */
  const ledgerPrintData = () => {
    const total = (type: string) => filtered.filter((r) => r.type === type).reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
    const income = total('Income');
    const expense = total('Expense');
    return {
      title: 'General Ledger',
      reference: [
        searchTerm && `Search: "${searchTerm}"`,
        `Printed ${formatDate(new Date().toISOString())}`
      ].filter(Boolean).join(' · '),
      rowCount: filtered.length,
      totalIncome: money(income),
      totalExpense: money(expense),
      net: money(income - expense),
      rows: filtered.map((r) => ({
        voucherId: r.id,
        date: formatDate(r.date),
        head: r.head,
        particulars: r.description || '',
        mode: r.mode,
        type: r.type,
        amount: money(r.amount)
      }))
    };
  };

  const bookColumns: Array<Column<BookEntry>> = [
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'id', header: 'Voucher', nowrap: true, render: (r) => <strong>{r.id}</strong> },
    { key: 'description', header: 'Particulars' },
    {
      key: 'receipt',
      header: 'Receipt',
      align: 'right',
      nowrap: true,
      render: (r) => (r.type === 'Income' ? <span style={{ color: t.income }}>{money(r.amount)}</span> : '-')
    },
    {
      key: 'payment',
      header: 'Payment',
      align: 'right',
      nowrap: true,
      render: (r) => (r.type === 'Expense' ? <span style={{ color: t.expense }}>{money(r.amount)}</span> : '-')
    },
    {
      key: 'runningBalance',
      header: 'Balance',
      align: 'right',
      nowrap: true,
      render: (r) => <strong>{money(r.runningBalance)}</strong>
    }
  ];

  const auditColumns: Array<Column<AuditEntry>> = [
    { key: 'origId', header: 'Original ID', nowrap: true, render: (r) => <strong>{r.origId}</strong> },
    { key: 'auditType', header: 'Source', render: (r) => <Badge tone="neutral">{r.auditType}</Badge> },
    { key: 'date', header: 'Txn Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'amount', header: 'Amount', align: 'right', nowrap: true, render: (r) => money(r.amount) },
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
    // The two-step trail: who asked, and which administrator approved.
    { key: 'deleteRequestedBy', header: 'Requested By', render: (r) => (
      <span style={{ fontSize: '12px' }}>{r.deleteRequestedBy || '-'}<br /><span style={{ color: t.textFaint }}>{r.deleteRequestedAt || ''}</span></span>
    ) },
    { key: 'deletedAt', header: 'Deleted On', render: (r) => (
      <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}{r.deleteApprovedBy && <><br />by {r.deleteApprovedBy}</>}</span>
    ) }
  ];

  const statementTable = (
    title: string,
    rows: HeadTotal[],
    total: number,
    tone: string
  ) => (
    <div style={{ flex: '1 1 340px' }}>
      <div style={styles.sectionTitle}>{title}</div>
      <DataTable
        minWidth="300px"
        columns={[
          { key: 'head', header: 'Account Head', render: (r) => <strong>{r.head}</strong> },
          { key: 'amount', header: 'Amount', align: 'right', nowrap: true, render: (r) => money(r.amount) }
        ]}
        rows={rows}
        emptyMessage="No entries in this period."
      />
      <div style={{
        display: 'flex', justifyContent: 'space-between', padding: '12px 15px',
        marginTop: '8px', borderRadius: t.radius.md,
        backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
        fontWeight: 'bold', color: tone
      }}>
        <span>Total {title}</span>
        <span>{money(total)}</span>
      </div>
    </div>
  );

  const kpiCards = [
    { label: 'Total Income', value: summary.totalIncome, accent: t.income },
    { label: 'Total Expenditure', value: summary.totalExpense, accent: t.danger },
    { label: 'Surplus / Deficit', value: summary.surplusDeficit, accent: summary.surplusDeficit >= 0 ? t.income : t.danger },
    { label: 'Closing Cash', value: summary.closingCash, accent: t.info },
    { label: 'Closing Bank', value: summary.closingBank, accent: t.info },
    { label: 'Closing Fund', value: summary.closingFund, accent: t.primary }
  ];

  const periodLabel = `${start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} – ${end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <div>
        <PageHeader
          icon="📊"
          title="Accounts"
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            { key: 'ledger', label: 'General Ledger' },
            { key: 'reports', label: 'Financial Reports' }
          ]}
        />
      </div>

      {/* Every figure below is a sum of the three sources. If one did not load,
          say so before the reader treats an incomplete total as the accounts. */}
      {missing.length > 0 && (
        <div
          role="alert"
         
          style={{
            margin: '0 0 16px', padding: '12px 16px', borderRadius: '8px',
            border: `1px solid ${t.danger}`, backgroundColor: `${t.danger}14`,
            color: t.textMain, fontSize: t.fontSize.base
          }}
        >
          <strong>These figures are incomplete.</strong>{' '}
          {missing.join(' and ')} could not be loaded, so every total on this screen
          is missing them. Reload once the connection is back before relying on these
          numbers or printing them.
        </div>
      )}

      {activeTab === 'ledger' && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); ledgerPage.reset(); }}
            onClear={() => { clear(); ledgerPage.reset(); }}
            placeholder="Search by Voucher ID, Account Head or Particulars..."
            actions={
              <PrintButton
                module="Accounts"
                documentType="ACCOUNTS_LEDGER"
                size="sm"
                title="General-Ledger"
                data={() => ledgerPrintData()}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            }
          />

          <TablePanel
            title="General Ledger"
            below={<Pagination {...ledgerPage} itemsOnPage={ledgerPage.currentItems.length} label="entries" />}
            columns={ledgerColumns}
            rows={ledgerPage.currentItems}
            rowKey={(r, i) => `${r.id}-${i}`}
            loading={loading}
            emptyMessage="No transactions recorded yet."
          />
        </>
      )}

      {activeTab === 'reports' && (
        <>
          <div style={{
            display: 'flex', gap: t.spacing.md, alignItems: 'flex-end', flexWrap: 'wrap',
            backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
            borderRadius: t.radius.lg, padding: t.spacing.md, marginBottom: t.spacing.lg
          }}>
            <Select
              label="Report" placeholder="" containerStyle={{ minWidth: '260px' }}
              options={REPORT_VIEWS}
              value={reportView}
              onChange={(e) => { setReportView(e.target.value); reportPage.reset(); }}
            />
            <Select
              label="Date Range" placeholder="" containerStyle={{ minWidth: '180px' }}
              options={REPORT_PERIODS}
              value={reportPeriod}
              onChange={(e) => { setReportPeriod(e.target.value); reportPage.reset(); }}
            />
            {reportPeriod === 'custom' && (
              <>
                <Input label="From" type="date" containerStyle={{ minWidth: '160px' }}
                  value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                <Input label="To" type="date" containerStyle={{ minWidth: '160px' }}
                  value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
              </>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <PrintButton
                module="Accounts"
                documentType={REPORT_DOCUMENTS[reportView] || 'FINANCIAL_SUMMARY'}
                title={`${REPORT_VIEWS.find((v) => v.value === reportView)?.label || 'Report'} ${periodLabel}`}
                data={() => reportPrintData()}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          {/* The statements print through the shared surface: same masthead,
              margins and pagination as every other document. */}
          <div>
            <h3 style={{ color: t.textMain, marginTop: 0 }}>
              {REPORT_VIEWS.find((v) => v.value === reportView)?.label}
              <span style={{ fontWeight: 'normal', color: t.textMuted, fontSize: t.fontSize.md }}> ({periodLabel})</span>
            </h3>

            {reportView === 'dashboard' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: t.spacing.md }}>
                  {kpiCards.map((kpi) => (
                    <div key={kpi.label} className="kpi-box" style={{
                      backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
                      borderLeft: `4px solid ${kpi.accent}`, borderRadius: t.radius.lg, padding: t.spacing.md
                    }}>
                      <div style={{ fontSize: '12px', color: kpi.accent, fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {kpi.label}
                      </div>
                      <div style={{ fontSize: '22px', fontWeight: 900, color: t.textMain }}>{money(kpi.value)}</div>
                    </div>
                  ))}
                </div>

                {/* Head-wise breakdown sits beside the tiles so the summary
                    answers "where did it come from / go" without switching view. */}
                <div style={{ display: 'flex', gap: t.spacing.lg, flexWrap: 'wrap', marginTop: t.spacing.xl }}>
                  {statementTable('Income by Head', heads.income, summary.totalIncome, t.income)}
                  {statementTable('Expenses by Head', heads.expense, summary.totalExpense, t.expense)}
                </div>
              </>
            )}

            {(reportView === 'cash_book' || reportView === 'bank_book') && (
              <>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', padding: '12px 15px',
                  marginBottom: '10px', borderRadius: t.radius.md,
                  backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`,
                  fontWeight: 'bold', color: t.textMain
                }}>
                  <span>Opening Balance</span>
                  <span>{money(reportView === 'cash_book' ? summary.openingCash : summary.openingBank)}</span>
                </div>

                <DataTable
                  columns={bookColumns}
                  rows={reportPage.currentItems}
                  rowKey={(r, i) => `${r.id}-${i}`}
                  loading={loading}
                  emptyMessage="No entries in this period."
                />

                <div style={{
                  display: 'flex', justifyContent: 'space-between', padding: '12px 15px',
                  marginTop: '10px', borderRadius: t.radius.md,
                  backgroundColor: t.primarySoft, border: `1px solid ${t.primaryBorder}`,
                  fontWeight: 'bold', color: t.primaryText
                }}>
                  <span>Closing Balance</span>
                  <span>{money(reportView === 'cash_book' ? summary.closingCash : summary.closingBank)}</span>
                </div>

                <Pagination {...reportPage} itemsOnPage={reportPage.currentItems.length} label="entries" />
              </>
            )}

            {reportView === 'trial_balance' && (
              <div style={{
                maxWidth: '820px', margin: '0 auto', border: `1px solid ${t.border}`,
                borderRadius: t.radius.lg, overflow: 'hidden', backgroundColor: t.bgMain
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: t.primary, color: t.textOnPrimary }}>
                      <th style={{ ...cellBase, textAlign: 'left' }}>Account Head / Particulars</th>
                      <th style={{ ...cellBase, textAlign: 'right' }}>Debit (Dr.)</th>
                      <th style={{ ...cellBase, textAlign: 'right' }}>Credit (Cr.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialBalance.rows.map((row, index) => (
                      row.isSection ? (
                        <tr key={row.label} style={{ backgroundColor: t.bgSubtle }}>
                          <td colSpan={3} style={{ ...cellBase, fontWeight: 'bold', color: t.textMain }}>
                            {row.label}
                          </td>
                        </tr>
                      ) : (
                        <tr key={`${row.label}-${index}`} style={{ borderBottom: `1px dashed ${t.border}` }}>
                          <td style={{ ...cellBase, paddingLeft: '30px', color: t.textMain }}>{row.label}</td>
                          <td style={{ ...cellBase, textAlign: 'right', color: t.textMain }}>
                            {row.debit !== undefined ? money(row.debit) : ''}
                          </td>
                          <td style={{ ...cellBase, textAlign: 'right', color: t.textMain }}>
                            {row.credit !== undefined ? money(row.credit) : ''}
                          </td>
                        </tr>
                      )
                    ))}
                    <tr style={{ backgroundColor: t.bgSubtle, borderTop: `2px solid ${t.primary}` }}>
                      <td style={{ ...cellBase, fontWeight: 'bold', textAlign: 'right', color: t.textMain }}>
                        GRAND TOTAL:
                      </td>
                      <td style={{ ...cellBase, textAlign: 'right', fontWeight: 'bold', color: t.textMain }}>
                        {money(trialBalance.totalDebit)}
                      </td>
                      <td style={{ ...cellBase, textAlign: 'right', fontWeight: 'bold', color: t.textMain }}>
                        {money(trialBalance.totalCredit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div style={{
                  padding: '10px', textAlign: 'center', fontSize: t.fontSize.sm,
                  color: trialBalance.balanced ? t.textFaint : t.danger
                }}>
                  {trialBalance.balanced
                    ? '* Trial Balance aggregates opening assets against equity and period transactions to verify double-entry parity.'
                    : '⚠ Debit and Credit totals do not agree — please review the underlying records.'}
                </div>
              </div>
            )}

            {reportView === 'pnl' && (
              <>
                <div style={{ display: 'flex', gap: t.spacing.lg, flexWrap: 'wrap' }}>
                  {statementTable('Income', heads.income, summary.totalIncome, t.income)}
                  {statementTable('Expenditure', heads.expense, summary.totalExpense, t.expense)}
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', padding: '14px 18px',
                  marginTop: t.spacing.lg, borderRadius: t.radius.lg,
                  backgroundColor: t.primarySoft, border: `1px solid ${t.primaryBorder}`,
                  fontWeight: 'bold', fontSize: t.fontSize.lg,
                  color: summary.surplusDeficit >= 0 ? t.income : t.danger
                }}>
                  <span>{summary.surplusDeficit >= 0 ? 'Surplus' : 'Deficit'} for the period</span>
                  <span>{money(summary.surplusDeficit)}</span>
                </div>
              </>
            )}

            {reportView === 'balance_sheet' && (
              <div style={{
                maxWidth: '900px', margin: '0 auto', border: `1px solid ${t.border}`,
                borderRadius: t.radius.lg, overflow: 'hidden', backgroundColor: t.bgMain
              }}>
                <div style={{ padding: '18px', textAlign: 'center', borderBottom: `2px solid ${t.primary}` }}>
                  <h2 style={{ margin: 0, color: t.textMain }}>Balance Sheet</h2>
                  <p style={{ margin: '5px 0 0 0', color: t.textMuted }}>
                    As on {end.toLocaleDateString('en-IN')}
                  </p>
                </div>

                {/* Liabilities & Funds on the left, Assets on the right — the
                    two totals must agree for the sheet to balance. */}
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    {
                      title: 'Liabilities & Funds',
                      section: 'Accumulated Funds',
                      rows: [
                        { label: 'Opening Balance', value: summary.openingFund },
                        {
                          label: summary.surplusDeficit >= 0 ? 'Add: Surplus' : 'Less: Deficit',
                          value: summary.surplusDeficit
                        }
                      ],
                      footLabel: 'TOTAL LIABILITIES',
                      footValue: summary.closingFund,
                      closing: { label: 'Closing General Fund', value: summary.closingFund }
                    },
                    {
                      title: 'Assets',
                      section: 'Current Assets',
                      rows: [
                        { label: 'Cash in Hand', value: summary.closingCash },
                        { label: 'Cash at Bank', value: summary.closingBank }
                      ],
                      footLabel: 'TOTAL ASSETS',
                      footValue: summary.closingCash + summary.closingBank,
                      closing: null
                    }
                  ].map((side, index) => (
                    <div key={side.title} style={{
                      flex: '1 1 340px',
                      borderRight: index === 0 ? `1px solid ${t.border}` : 'none'
                    }}>
                      <div style={{
                        ...cellBase, fontWeight: 'bold', color: t.textOnPrimary,
                        backgroundColor: t.primary
                      }}>
                        {side.title}
                      </div>
                      <div style={{
                        ...cellBase, fontWeight: 'bold', color: t.textMain,
                        backgroundColor: t.bgSubtle, borderBottom: `1px solid ${t.border}`
                      }}>
                        {side.section}
                      </div>
                      {side.rows.map((row) => (
                        <div key={row.label} style={{
                          ...cellBase, display: 'flex', justifyContent: 'space-between',
                          paddingLeft: '30px', color: t.textMain
                        }}>
                          <span>{row.label}</span>
                          <span>{money(row.value)}</span>
                        </div>
                      ))}
                      {side.closing && (
                        <div style={{
                          ...cellBase, display: 'flex', justifyContent: 'space-between',
                          fontWeight: 'bold', color: t.textMain, borderTop: `1px dashed ${t.border}`
                        }}>
                          <span>{side.closing.label}</span>
                          <span>{money(side.closing.value)}</span>
                        </div>
                      )}
                      <div style={{
                        ...cellBase, display: 'flex', justifyContent: 'space-between',
                        fontWeight: 'bold', color: t.textMain, backgroundColor: t.bgSubtle,
                        borderTop: `2px solid ${t.primary}`
                      }}>
                        <span>{side.footLabel}:</span>
                        <span>{money(side.footValue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reportView === 'audit' && (
              <>
                <div style={{
                  backgroundColor: t.bgSubtle, padding: '15px', borderRadius: t.radius.lg,
                  border: `1px solid ${t.warning}`, marginBottom: t.spacing.lg
                }}>
                  <h3 style={{ margin: '0 0 10px 0', color: t.warning }}>Internal Audit Alerts</h3>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: t.textMain, fontSize: t.fontSize.md }}>
                    <li>
                      <strong>High Value Transactions (&gt; {money(HIGH_VALUE_THRESHOLD)}):</strong>{' '}
                      {auditAlerts.highValueCount} found in this period.
                    </li>
                    <li>
                      <strong>Voided/Deleted Records:</strong>{' '}
                      {auditAlerts.voidedCount} actions recorded in this period.
                    </li>
                  </ul>
                </div>

                <h3 style={{ color: t.textMain }}>Deleted / Voided Transaction Log</h3>

                <DataTable
                  columns={auditColumns}
                  rows={reportPage.currentItems as AuditEntry[]}
                  rowKey={(r, i) => `${r.origId}-${i}`}
                  loading={loading}
                  emptyMessage="No voided records."
                />
                <Pagination {...reportPage} itemsOnPage={reportPage.currentItems.length} label="voided records" />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Accounts;
